import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import BottomNav from '@/components/BottomNav'
import LoadingSpinner from '@/components/LoadingSpinner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { subscriptionPlanService } from '@/services/subscriptionPlanService'
import { customerService } from '@/services/firebase'
import { checkoutRequestService } from '@/services/checkoutRequestService'
import { washCatalogService } from '@/services/washCatalogService'
import toast from 'react-hot-toast'
import { CreditCard, ClipboardCopy } from 'lucide-react'

function money(n) {
  const v = Number(n || 0)
  return `R${v.toFixed(2)}`
}

export default function CustomerSubscriptions() {
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [plans, setPlans] = useState([])
  const [washCatalog, setWashCatalog] = useState(null)
  const [creatingId, setCreatingId] = useState(null)
  const [checkoutCode, setCheckoutCode] = useState(null)

  useEffect(() => {
    ;(async () => {
      if (!currentUser) return
      setLoading(true)
      try {
        const catalog = await washCatalogService.get()
        setWashCatalog(catalog)
        // Show all plans (across branches) for customer browsing.
        const list = await subscriptionPlanService.listPlans('all-branches')
        setPlans((list || []).filter((p) => p.isActive !== false))
      } catch (e) {
        console.error(e)
        toast.error('Failed to load subscription packages')
      } finally {
        setLoading(false)
      }
    })()
  }, [currentUser])

  const tierLabelByKey = useMemo(() => {
    const out = new Map()
    const pkgs = Array.isArray(washCatalog?.packages) ? washCatalog.packages : []
    for (const p of pkgs) {
      for (const t of Array.isArray(p.tiers) ? p.tiers : []) {
        out.set(`${p.id}:${t.id}`, `${p.name} · ${t.label || t.id}`)
      }
    }
    return out
  }, [washCatalog])

  const serviceNameById = useMemo(() => {
    const out = new Map()
    const pkgs = Array.isArray(washCatalog?.packages) ? washCatalog.packages : []
    for (const p of pkgs) out.set(String(p.id), String(p.name || p.id))
    return out
  }, [washCatalog])

  const coveredSummary = (plan) => {
    const tierKeys = Array.isArray(plan?.eligibleTierIds) ? plan.eligibleTierIds : []
    if (tierKeys.length > 0) {
      const labels = tierKeys
        .map((k) => tierLabelByKey.get(k) || k)
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b)))
      return labels.length > 0 ? labels.join(', ') : null
    }

    const svcIds = Array.isArray(plan?.eligibleServiceIds) ? plan.eligibleServiceIds : []
    if (svcIds.length > 0) {
      const names = svcIds
        .map((id) => serviceNameById.get(String(id)) || String(id))
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b)))
      return names.length > 0 ? names.join(', ') : null
    }

    return null
  }

  const sortedPlans = useMemo(() => {
    // Dedupe by code (preferred) or by a stable composite key.
    const bestByKey = new Map()
    for (const p of plans) {
      const key =
        String(p.code || '').trim() ||
        [
          p.name,
          p.amount,
          p.durationDays,
          p.washesIncluded,
          Array.isArray(p.eligibleServiceIds) ? p.eligibleServiceIds.sort().join(',') : '',
          Array.isArray(p.eligibleTierIds) ? p.eligibleTierIds.sort().join(',') : '',
        ].join('|')
      const prev = bestByKey.get(key)
      if (!prev) {
        bestByKey.set(key, p)
        continue
      }
      // Prefer most recently updated plan when duplicates exist
      const prevT = prev?.updatedAt?.toDate?.() ? prev.updatedAt.toDate().getTime() : new Date(prev?.updatedAt || 0).getTime()
      const nextT = p?.updatedAt?.toDate?.() ? p.updatedAt.toDate().getTime() : new Date(p?.updatedAt || 0).getTime()
      if ((nextT || 0) > (prevT || 0)) bestByKey.set(key, p)
    }
    return Array.from(bestByKey.values()).sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0))
  }, [plans])

  const createRequest = async (plan) => {
    if (!currentUser) return
    try {
      setCreatingId(plan.id)
      await customerService.ensureCustomerProfileForUser(currentUser)
      const customer = await customerService.getCustomerByUserId(currentUser.uid)
      if (!customer?.id) {
        toast.error('Please complete your profile first')
        return
      }

      const id = await checkoutRequestService.createSubscriptionRequest({
        userUid: currentUser.uid,
        userEmail: currentUser.email,
        customerId: customer.id,
        customerName: customer.name || currentUser.displayName || '',
        plan,
      })
      setCheckoutCode(id)
      toast.success('Request created. Show this code at the cashier.')
    } catch (e) {
      console.error(e)
      toast.error('Could not create checkout request')
    } finally {
      setCreatingId(null)
    }
  }

  const copyCode = async () => {
    if (!checkoutCode) return
    try {
      await navigator.clipboard.writeText(checkoutCode)
      toast.success('Copied checkout code')
    } catch (e) {
      console.error(e)
      toast.error('Could not copy')
    }
  }

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center bg-background">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-32">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
        <div className="space-y-2 border-b border-border pb-6">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Customer</p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Subscription packages</h1>
          <p className="text-sm text-muted-foreground max-w-lg">
            Choose a package, then show the checkout code at the cashier to activate it.
          </p>
        </div>

        {checkoutCode && (
          <Card className="border-border/80 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="size-4" />
                Your checkout code
              </CardTitle>
              <CardDescription>Show this to the cashier on POS.</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-3">
              <div className="font-mono text-sm rounded-lg border border-border px-3 py-2">
                {checkoutCode}
              </div>
              <Button variant="outline" onClick={copyCode}>
                <ClipboardCopy className="size-4" />
                Copy
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {sortedPlans.map((p) => (
            <Card key={p.id} className="border-border/80 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{p.name}</CardTitle>
                <CardDescription>
                  {p.washesIncluded === 'unlimited'
                    ? 'Unlimited washes'
                    : `${Number(p.washesIncluded || 0)} washes included`}
                  {Number(p.durationDays || 0) > 0 ? ` · ${Number(p.durationDays)} days` : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {coveredSummary(p) && (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground/90">Covered for:</span>{' '}
                    {coveredSummary(p)}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Price</span>
                  <span className="text-lg font-semibold tabular-nums text-foreground">{money(p.amount)}</span>
                </div>
                {p.isActive === false ? (
                  <Badge variant="secondary">Inactive</Badge>
                ) : (
                  <Button
                    className="w-full"
                    onClick={() => createRequest(p)}
                    disabled={creatingId === p.id}
                  >
                    {creatingId === p.id ? 'Creating…' : 'Add to checkout'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      <BottomNav />
    </div>
  )
}

